import React, { useState, useEffect, useRef, useCallback } from "react";
import moment from "moment";
import ls from "local-storage";
import AuthApi from "../../Services/Authapi";
import "./ReminderPopup.css";
import {
  getEventId,
  getEventTitle,
  getCleanEventTitle,
  getCategory,
  getEffectiveCategory,
  getEventStartMoment,
  getEventOwnerId,
  getLeaveOwnerName,
  getLeaveStatus,
  formatMeetingTitle,
  isMeetingRecipient,
  getStableEventKey
} from "../../../utils/calendarReminderUtils";

const STORAGE_PREFIX = "ems_reminder_dismissed_";
const TIMESTAMP_STORAGE_PREFIX = "ems_reminder_time_";
const CHECK_INTERVAL_MS = 30 * 1000;
const FETCH_RANGE_PAST_DAYS = 1;
const FETCH_RANGE_FUTURE_DAYS = 3;
const MEETING_24H_WINDOW_MIN = 1439.0;
const MEETING_24H_WINDOW_MAX = 1440.5;

const getReminderKey = (type, eventId, dateStr) =>
  `${STORAGE_PREFIX}${type}_${eventId}_${dateStr}`;

const getReminderTimeStorageKey = (key) =>
  `${TIMESTAMP_STORAGE_PREFIX}${key}`;

const isAlreadyDismissed = (key) => {
  try {
    return localStorage.getItem(key) === "1";
  } catch {
    return false;
  }
};

const markAsDismissed = (key) => {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // ignore
  }
};

const getStableReminderTimeLabel = (key, fallbackMoment) => {
  const fallbackLabel = fallbackMoment.format("h:mm A");
  try {
    const storageKey = getReminderTimeStorageKey(key);
    const stored = localStorage.getItem(storageKey);
    if (stored) {
      return stored;
    }
    localStorage.setItem(storageKey, fallbackLabel);
    return fallbackLabel;
  } catch {
    return fallbackLabel;
  }
};

const purgeOldDismissedKeys = () => {
  try {
    const today = moment().format("YYYY-MM-DD");
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(STORAGE_PREFIX) || key.startsWith(TIMESTAMP_STORAGE_PREFIX))) {
        const parts = key.split("_");
        const dateStr = parts[parts.length - 1];
        if (
          dateStr &&
          dateStr.match(/^\d{4}-\d{2}-\d{2}$/) &&
          moment(dateStr).isBefore(moment(today).subtract(7, "days"))
        ) {
          keysToRemove.push(key);
        }
      }
    }
    keysToRemove.forEach((key) => localStorage.removeItem(key));
  } catch {
    // ignore
  }
};

const buildReminders = (events = [], isAdmin = false, currentUserId = null) => {
  const now = moment();
  const todayStr = now.format("YYYY-MM-DD");
  const reminders = [];

  events.forEach((event) => {
    const category = getCategory(event);
    const effectiveCategory = getEffectiveCategory(event);
    const eventId =
      getEventId(event) ?? getEventTitle(event).replace(/\s+/g, "-").toLowerCase();
    const title = getCleanEventTitle(event);

    if (category === "birthday" || category === "bday") {
      const startMoment = getEventStartMoment(event, true);
      if (!startMoment.isValid()) return;

      const thisYearBday = startMoment.clone().year(now.year());
      if (thisYearBday.format("MM-DD") === now.format("MM-DD")) {
        const key = getReminderKey("birthday", eventId, todayStr);
        const timeChipLabel = getStableReminderTimeLabel(key, now);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `birthday-${eventId}-${todayStr}`,
            dismissKey: key,
            type: "birthday",
            category: "birthday",
            icon: "B",
            typeLabel: "Birthday Today",
            title,
            subtitle: "Wishing a very Happy Birthday!",
            timeChip: timeChipLabel,
            urgent: false,
            sortWeight: 10,
          });
        }
      }
      return;
    }

    if (effectiveCategory === "holiday") {
      const startMoment = getEventStartMoment(event, true);
      if (!startMoment.isValid()) return;

      const diffDays = startMoment
        .clone()
        .startOf("day")
        .diff(now.clone().startOf("day"), "days");

      if (diffDays === 2 || diffDays === 1) {
        const reminderType = `holiday-${diffDays}d`;
        const key = getReminderKey(reminderType, eventId, todayStr);
        const timeChipLabel = getStableReminderTimeLabel(key, now);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `${reminderType}-${eventId}-${todayStr}`,
            dismissKey: key,
            type: "holiday",
            category: "holiday",
            icon: "H",
            typeLabel: "Upcoming Holiday",
            title,
            subtitle:
              diffDays === 1
                ? `Tomorrow - ${startMoment.format("ddd, MMM D")}`
                : `In 2 days - ${startMoment.format("ddd, MMM D")}`,
            timeChip: timeChipLabel,
            urgent: false,
            sortWeight: 20,
          });
        }
      }
      return;
    }

    if (effectiveCategory === "leave") {
      const startMoment = getEventStartMoment(event, true);
      if (!startMoment.isValid()) return;

      const diffDays = startMoment
        .clone()
        .startOf("day")
        .diff(now.clone().startOf("day"), "days");

      if (diffDays !== 1) return;

      const leaveStatus = getLeaveStatus(event);

      if (leaveStatus !== "approved" && leaveStatus !== "pending") return;

      if (leaveStatus === "pending" && !isAdmin) return;

      const leaveOwnerId = getEventOwnerId(event);
      const isMyLeave =
        currentUserId !== null &&
        leaveOwnerId !== null &&
        String(leaveOwnerId) === String(currentUserId);

      if (!isAdmin && !isMyLeave) return;

      const leaveOwnerName = getLeaveOwnerName(event);
      const key = getReminderKey(`leave-1d-${leaveStatus}`, eventId, todayStr);
      const timeChipLabel = getStableReminderTimeLabel(key, now);
      if (!isAlreadyDismissed(key)) {
        const cleanTitle = (title || "Leave")
          .replace(/^(leave|reminder)\s*[-:]\s*/i, "")
          .replace(/\bPending\s+Leave\b/i, "Leave")
          .replace(/\bApproved\s+Leave\b/i, "Leave")
          .trim() || "Leave";

        const displayTitle =
          isAdmin && leaveOwnerName && !isMyLeave
            ? `${leaveOwnerName} - ${cleanTitle}`
            : cleanTitle;

        reminders.push({
          id: `leave-1d-${eventId}-${todayStr}`,
          dismissKey: key,
          type: "leave",
          category: "leave",
          icon: "L",
          typeLabel:
            leaveStatus === "pending"
              ? "Pending Leave Tomorrow"
              : (isAdmin && !isMyLeave ? "Employee Leave Tomorrow" : "Your Leave Tomorrow"),
          title: displayTitle,
          subtitle: `Starts tomorrow - ${startMoment.format("ddd, MMM D")}`,
          timeChip: timeChipLabel,
          urgent: false,
          sortWeight: 30,
        });
      }
      return;
    }

    if (effectiveCategory === "meeting") {
      const startMoment = getEventStartMoment(event, false);
      if (!startMoment.isValid()) return;

      if (!isMeetingRecipient(event, { isAdmin, currentUserId })) return;

      const minutesUntil = startMoment.diff(now, "minutes", true);
      const displayMinutesUntil = Math.max(1, Math.ceil(minutesUntil));
      const stableKey = getStableEventKey(event);
      const autoOpenKey = `meeting-soon-${stableKey}-${todayStr}-${startMoment.format("HH-mm")}`;
      const meetingTitle = formatMeetingTitle(event);

      if (minutesUntil >= 10 && minutesUntil <= 1440) {
        const key = getReminderKey("meeting-24h", stableKey, startMoment.format("YYYY-MM-DD-HH-mm"));
        const timeChipLabel = getStableReminderTimeLabel(key, now);
        if (!isAlreadyDismissed(key)) {
          reminders.push({
            id: `meeting-24h-${stableKey}-${startMoment.format("YYYY-MM-DD-HH-mm")}`,
            dismissKey: key,
            type: "meeting",
            category: "meeting",
            icon: "M",
            typeLabel: "Meeting In 24 Hours",
            title: meetingTitle,
            subtitle: `${startMoment.format("ddd, MMM D")} at ${startMoment.format("h:mm A")}`,
            timeChip: timeChipLabel,
            urgent: false,
            sortWeight: 40,
          });
        }
      }

      if (minutesUntil >= 0 && minutesUntil <= 10) {
        const soonKey = getReminderKey(
          "meeting-soon",
          stableKey,
          startMoment.format("YYYY-MM-DD-HH-mm")
        );
        const timeChipLabel = getStableReminderTimeLabel(autoOpenKey, now);
        if (!isAlreadyDismissed(soonKey)) {
          reminders.push({
            id: `meeting-soon-${stableKey}-${startMoment.format("YYYY-MM-DD-HH-mm")}`,
            dismissKey: soonKey,
            autoOpen: displayMinutesUntil === 10,
            autoOpenKey,
            type: "meeting-soon",
            category: "meeting-soon",
            icon: "",
            typeLabel: "Meeting Starting Soon!",
            title: meetingTitle,
            subtitle: `Starts in ${displayMinutesUntil} minute${displayMinutesUntil === 1 ? "" : "s"}`,
            timeChip: timeChipLabel,
            urgent: true,
            sortWeight: 1,
          });
        }
      }
    }
  });

  reminders.sort((a, b) => {
    if (a.urgent !== b.urgent) return a.urgent ? -1 : 1;
    return a.sortWeight - b.sortWeight;
  });

  return reminders;
};

const BellIcon = () => (
  <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.9 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z" />
  </svg>
);

const ReminderPopup = ({ events = [], isAdmin = false, currentUser = null }) => {
  const [reminders, setReminders] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [fetchedEvents, setFetchedEvents] = useState([]);
  const intervalRef = useRef(null);
  const fetchIntervalRef = useRef(null);
  const seenAutoOpenKeysRef = useRef(new Set());
  const blockedAutoOpenKeysRef = useRef(new Set());

  const resolvedCurrentUser = currentUser || ls("user") || null;
  const currentUserId = resolvedCurrentUser?.id ?? resolvedCurrentUser?.Id ?? null;
  const roles = ls("roles");
  const resolvedIsAdmin = Boolean(
    isAdmin ||
    (Array.isArray(roles)
      ? roles.some((role) => String(role).toLowerCase().includes("admin"))
      : String(roles || "").toLowerCase().includes("admin"))
  );
  const hasExternalEvents = Array.isArray(events) && events.length > 0;
  const activeEvents = hasExternalEvents ? events : fetchedEvents;

  const fetchReminderEvents = useCallback(async () => {
    if (hasExternalEvents) {
      return;
    }

    try {
      const now = moment();
      const response = await AuthApi.getDashboardCalendar({
        start: now.clone().subtract(FETCH_RANGE_PAST_DAYS, "day").format("YYYY-MM-DD"),
        end: now.clone().add(FETCH_RANGE_FUTURE_DAYS, "days").format("YYYY-MM-DD"),
      });

      if (response && response.status === true && response.data) {
        const nextEvents = Array.isArray(response.data.events) ? response.data.events : [];
        setFetchedEvents(nextEvents);
      }
    } catch (error) {
      console.error("Error fetching reminder events:", error);
    }
  }, [hasExternalEvents]);

  const computeReminders = useCallback(() => {
    const computed = buildReminders(activeEvents, resolvedIsAdmin, currentUserId);
    setReminders(computed);
  }, [activeEvents, resolvedIsAdmin, currentUserId]);

  useEffect(() => {
    purgeOldDismissedKeys();
  }, []);

  useEffect(() => {
    computeReminders();
  }, [computeReminders]);

  useEffect(() => {
    if (hasExternalEvents) {
      return undefined;
    }

    fetchReminderEvents();
    fetchIntervalRef.current = setInterval(fetchReminderEvents, CHECK_INTERVAL_MS);
    return () => clearInterval(fetchIntervalRef.current);
  }, [fetchReminderEvents, hasExternalEvents]);

  useEffect(() => {
    intervalRef.current = setInterval(computeReminders, CHECK_INTERVAL_MS);
    return () => clearInterval(intervalRef.current);
  }, [computeReminders]);

  useEffect(() => {
    const autoOpenReminders = reminders.filter((reminder) => reminder.autoOpen && reminder.autoOpenKey);
    const activeAutoOpenKeys = new Set(autoOpenReminders.map((reminder) => reminder.autoOpenKey));
    let shouldAutoOpen = false;

    autoOpenReminders.forEach((reminder) => {
      if (
        !seenAutoOpenKeysRef.current.has(reminder.autoOpenKey) &&
        !blockedAutoOpenKeysRef.current.has(reminder.autoOpenKey)
      ) {
        seenAutoOpenKeysRef.current.add(reminder.autoOpenKey);
        shouldAutoOpen = true;
      }
    });

    Array.from(seenAutoOpenKeysRef.current).forEach((key) => {
      if (!activeAutoOpenKeys.has(key)) {
        seenAutoOpenKeysRef.current.delete(key);
        blockedAutoOpenKeysRef.current.delete(key);
      }
    });

    if (shouldAutoOpen) {
      setIsOpen(true);
    }
  }, [reminders]);

  const closePanel = () => {
    reminders.forEach((reminder) => {
      if (reminder.autoOpen && reminder.autoOpenKey) {
        blockedAutoOpenKeysRef.current.add(reminder.autoOpenKey);
      }
    });
    setIsOpen(false);
  };

  const handleDismiss = (reminder) => {
    markAsDismissed(reminder.dismissKey);
    if (reminder.autoOpen && reminder.autoOpenKey) {
      blockedAutoOpenKeysRef.current.add(reminder.autoOpenKey);
    }
    setReminders((prev) => prev.filter((item) => item.id !== reminder.id));
  };

  const handleDismissAll = () => {
    reminders.forEach((reminder) => {
      markAsDismissed(reminder.dismissKey);
      if (reminder.autoOpen && reminder.autoOpenKey) {
        blockedAutoOpenKeysRef.current.add(reminder.autoOpenKey);
      }
    });
    setReminders([]);
    setIsOpen(false);
  };

  const unreadCount = reminders.length;

  return (
    <>
      <button
        className="ems-reminder-bell-btn"
        onClick={() => setIsOpen((prev) => !prev)}
        title="Reminders"
        aria-label={`Reminders (${unreadCount} pending)`}
      >
        <BellIcon />
        {unreadCount > 0 && (
          <span className="ems-reminder-bell-badge">
            {unreadCount > 99 ? "99+" : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="ems-reminder-panel-overlay" onClick={closePanel} />
          <div className="ems-reminder-panel" role="dialog" aria-label="Reminders panel">
            <div className="ems-reminder-panel-header">
              <div className="ems-reminder-panel-header-left">
                <div className="ems-reminder-panel-header-icon">R</div>
                <div>
                  <h3>Reminders</h3>
                  <p>
                    {unreadCount === 0
                      ? "All caught up!"
                      : `${unreadCount} pending reminder${unreadCount > 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>
              <div className="ems-reminder-panel-header-actions">
                {unreadCount > 0 && (
                  <button
                    className="ems-reminder-panel-clear-btn"
                    onClick={handleDismissAll}
                    title="Dismiss all reminders"
                  >
                    Clear All
                  </button>
                )}
                <button
                  className="ems-reminder-panel-close-btn"
                  onClick={closePanel}
                  title="Close panel"
                >
                  x
                </button>
              </div>
            </div>

            <div className="ems-reminder-panel-body">
              {reminders.length === 0 ? (
                <div className="ems-reminder-empty">
                  <div className="ems-reminder-empty-icon">OK</div>
                  <p>No reminders right now.</p>
                  <p style={{ fontSize: "12px", marginTop: "4px", color: "#b0bec5" }}>
                    Check back later!
                  </p>
                </div>
              ) : (
                reminders.map((reminder) => (
                  <ReminderCard
                    key={reminder.id}
                    reminder={reminder}
                    onDismiss={handleDismiss}
                  />
                ))
              )}
            </div>
          </div>
        </>
      )}
    </>
  );
};

const ReminderCard = ({ reminder, onDismiss }) => (
  <div className={`ems-reminder-card ems-reminder-card-${reminder.category}`}>
    <div className="ems-reminder-card-stripe" />
    <div className="ems-reminder-card-inner">
      <div className="ems-reminder-card-row1">
        <span className="ems-reminder-card-icon">{reminder.icon}</span>
        <div className="ems-reminder-card-text">
          <div className="ems-reminder-card-type-label">{reminder.typeLabel}</div>
          <div className="ems-reminder-card-title">{reminder.title}</div>
          {reminder.subtitle && <div className="ems-reminder-card-subtitle">{reminder.subtitle}</div>}
        </div>
        <button
          className="ems-reminder-card-dismiss-btn"
          onClick={() => onDismiss(reminder)}
          title="Dismiss this reminder"
          aria-label="Dismiss reminder"
        >
          x
        </button>
      </div>
      <div className="ems-reminder-card-footer">
        <span className="ems-reminder-card-time-chip">{reminder.timeChip}</span>
      </div>
    </div>
  </div>
);

export default ReminderPopup;
